import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getPublishingDetail,
  approvePublishing,
  rejectPublishing,
  publishPublishing,
  previewPublishing,
} from "../../../api/adminClientPublishingApi";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import StatusBadge from "../../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../../components/midpoint/admin/users/adminUserFormatters";
import RequirePermission from "../../../auth/RequirePermission";

function AdminClientPublishingReviewPageContent() {
  const { contentVersionId } = useParams();
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reviewComments, setReviewComments] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [detailBody, previewBody] = await Promise.all([
        getPublishingDetail(contentVersionId),
        previewPublishing(contentVersionId).catch(() => null),
      ]);
      setDetail(detailBody);
      setPreview(previewBody);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load submission.");
    } finally {
      setLoading(false);
    }
  }, [contentVersionId]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action) => {
    setActing(true);
    setMessage("");
    setErrorMessage("");
    try {
      if (action === "approve") {
        await approvePublishing(contentVersionId, { reviewComments });
        setMessage("Approved.");
      } else if (action === "reject") {
        await rejectPublishing(contentVersionId, { reviewComments });
        setMessage("Rejected. Client will see your comments.");
      } else if (action === "publish") {
        if (!window.confirm("Publish this content to the public site?")) {
          setActing(false);
          return;
        }
        await publishPublishing(contentVersionId);
        setMessage("Published successfully.");
      }
      await load();
    } catch (error) {
      setErrorMessage(error.message || "Action failed.");
    } finally {
      setActing(false);
    }
  };

  if (loading) return <AdminStatePanel message="Loading submission…" />;
  if (errorMessage && !detail) return <AdminStatePanel variant="error" message={errorMessage} />;

  const version = detail?.version;
  const snapshot = detail?.snapshot || preview?.snapshot || {};

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={version?.title || "Review submission"}
        description={`${version?.contentType} · ${version?.organizationName || ""}`}
        actions={
          <Link to="/admin/client-publishing/pending" className="cv-button-link">
            Back to queue
          </Link>
        }
      />

      {message ? <p className="panel-card muted">{message}</p> : null}
      {errorMessage ? <AdminStatePanel variant="error" message={errorMessage} /> : null}

      <section className="panel-card">
        <StatusBadge tone="warning" label={version?.publishStatusLabel} />
        <dl className="client-publishing-panel__meta">
          <dt>Client View</dt>
          <dd>{version?.clientViewName || version?.clientViewId}</dd>
          <dt>Submitted</dt>
          <dd>
            {version?.submittedAt ? formatDateTime(version.submittedAt) : "—"}
            {version?.submittedByEmail ? ` · ${version.submittedByEmail}` : ""}
          </dd>
        </dl>
      </section>

      <section className="panel-card">
        <h3>Preview</h3>
        <p className="muted">Submitted version (not live until published)</p>
        <pre className="client-preview-json">
          {JSON.stringify(snapshot, null, 2)}
        </pre>
        {/* TODO: rich visual diff viewer */}
      </section>

      {detail?.publishedVersion ? (
        <section className="panel-card">
          <h3>Currently published</h3>
          <p className="muted">Version {detail.publishedVersion.versionNumber}</p>
        </section>
      ) : null}

      <RequirePermission permission="client_admin.manage_publishing">
        <section className="panel-card">
          <label className="admin-users-field">
            <span>Review comments</span>
            <textarea
              value={reviewComments}
              onChange={(e) => setReviewComments(e.target.value)}
              rows={4}
              placeholder="Required when rejecting"
            />
          </label>
          <div className="client-view-card__actions">
            <button
              type="button"
              className="midpoint-share-button"
              disabled={acting || version?.publishStatus !== "PENDING_REVIEW"}
              onClick={() => runAction("approve")}
            >
              Approve
            </button>
            <button
              type="button"
              className="midpoint-share-button secondary"
              disabled={acting || version?.publishStatus !== "PENDING_REVIEW"}
              onClick={() => runAction("reject")}
            >
              Reject
            </button>
            <button
              type="button"
              className="midpoint-share-button"
              disabled={
                acting || !["APPROVED", "PENDING_REVIEW"].includes(version?.publishStatus)
              }
              onClick={() => runAction("publish")}
            >
              Publish
            </button>
          </div>
        </section>
      </RequirePermission>
    </AdminPageLayout>
  );
}

export default function AdminClientPublishingReviewPage() {
  return <AdminClientPublishingReviewPageContent />;
}
